import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [timeoutExpired, setTimeoutExpired] = useState(false);

  // Set a timeout to prevent infinite loading
  useEffect(() => {
    console.log('ProtectedRoute mounted, auth state:', { loading, user });
    
    const timer = setTimeout(() => {
      if (loading) {
        console.log('Auth check timeout expired after 3 seconds, forcing navigation');
        setTimeoutExpired(true);
      }
    }, 3000); // reduce to 3 seconds timeout

    return () => {
      console.log('ProtectedRoute cleanup, clearing timeout');
      clearTimeout(timer);
    };
  }, [loading]);

  // Handle navigation when authentication fails
  useEffect(() => {
    if (!loading) {
      if (!user) {
        console.log('No authenticated user, redirecting to login');
        navigate('/login', { replace: true });
      } else {
        console.log('User authenticated:', user.id);
      }
    }
  }, [user, loading, navigate]);

  // Show loading state if still checking auth and timeout hasn't expired
  if (loading && !timeoutExpired) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-vibebops-accent rounded-full border-t-transparent"></div>
    </div>;
  }

  // If not loading and no user, or timeout expired, redirect to login
  if ((!loading && !user) || timeoutExpired) {
    console.log('Redirecting to login page', { loading, user, timeoutExpired });
    return <Navigate to="/login" replace />;
  }

  // If we have a user, render the children
  return <>{children}</>;
} 